import React, { type FC } from "react";
import { formatMonthlyFinancePrice, mapPrice } from "../../../../../../shared-logic/utils/priceUtils";
import {
    CommonMonthlyPriceLabelKey,
    type FormatMonthlyPriceType,
} from "../../../../../../../../common-deprecated/priceUtils";
import { AemFmComponent } from "../../../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { useGradeSelectorSelector } from "../../../../../redux/hooks";
import {
    getAemMonthlyRateFormat,
    showAemMonthlyRates,
} from "../../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import Popover from "../../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import type {
    ApiGradeBodyEngineTransmissionWheeldriveCarType,
    ApiPriceType,
    SBRockarCarType,
} from "../../../../../../../../common-deprecated/types/ApiModel";
import { RequestStatusEnum } from "../../../../../../../../common-deprecated/types/CommonTypes";
import styles from "./Price.module.scss";

const MonthlyPrice: FC<{
    car: ApiGradeBodyEngineTransmissionWheeldriveCarType | SBRockarCarType;
    selectedColourId?: string;
    price: ApiPriceType;
}> = ({ car, selectedColourId, price }) => {
    const commonSettings = useGradeSelectorSelector((state) => state.commonSettings);
    const modelCode = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.code);
    const monthlyEnabled = showAemMonthlyRates(commonSettings, AemFmComponent.GradeSelector, "new", modelCode);
    const { state, cars } = useGradeSelectorSelector((gss) => gss.gradeSelectorFinance);
    const financeId = `${(car as ApiGradeBodyEngineTransmissionWheeldriveCarType).CarID || (car as SBRockarCarType).vehicle.base.externalId}-${selectedColourId as string}`;

    if (!monthlyEnabled) {
        return null;
    }

    if (state === RequestStatusEnum.Fetching) {
        return <span className={styles.loadbar} style={{ width: "100px", height: "20px" }} />;
    }

    const carFinance = cars[financeId];

    if (!carFinance) {
        return null;
    }

    const { financeInfo, quote } = carFinance;
    const monthlyPriceFormat = getAemMonthlyRateFormat(commonSettings, AemFmComponent.GradeSelector, "new", modelCode);
    const { labels = [], secondaryLabels = [] } = financeInfo
        ? formatMonthlyFinancePrice<FormatMonthlyPriceType>(
              mapPrice(price, financeInfo),
              financeInfo,
              commonSettings,
              monthlyPriceFormat,
              undefined,
              undefined,
              undefined,
              true,
          )
        : { labels: [] };

    if (labels.length === 0) {
        return null;
    }

    return (
        <span>
            {labels.map(({ key, value }) => (
                <span key={key}>
                    {(key === CommonMonthlyPriceLabelKey.Price || key === CommonMonthlyPriceLabelKey.DownPayment) && (
                        <span dangerouslySetInnerHTML={{ __html: value }} />
                    )}
                </span>
            ))}

            {secondaryLabels && secondaryLabels.length > 0 && <span> / </span>}

            {secondaryLabels.map(({ key, value }) => (
                <span key={key}>
                    {key === CommonMonthlyPriceLabelKey.Price && <span dangerouslySetInnerHTML={{ __html: value }} />}
                </span>
            ))}

            {!commonSettings.hideFinanceTooltip && quote && (
                <Popover placement="top-start">
                    <Popover.Trigger>
                        <Button variant="clean" type="button">
                            <Icon variant="info" sizing="md" aria-hidden="true" />
                        </Button>
                    </Popover.Trigger>
                    <Popover.Content>
                        <div>
                            {quote.productName && <strong>{quote.productName}</strong>}
                            {quote.calculations && quote.calculations.length !== 0 && (
                                <table>
                                    <tbody>
                                        {quote.calculations.map(({ name, value, format }) => (
                                            <tr style={{ color: format?.colour }} key={name}>
                                                <td>{format?.b ? <strong>{name}</strong> : <span>{name}</span>}</td>
                                                <td>
                                                    {format?.b ? (
                                                        <strong dangerouslySetInnerHTML={{ __html: value }} />
                                                    ) : (
                                                        <span dangerouslySetInnerHTML={{ __html: value }} />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <div dangerouslySetInnerHTML={{ __html: quote.value }} />
                        </div>
                    </Popover.Content>
                </Popover>
            )}
        </span>
    );
};

export default MonthlyPrice;
